import {useSyncExternalStore} from 'use-sync-external-store/shim';

import {safeLocalStorage} from '../../util/localStorage';

const WBAGENT_NOTICES_PREFIX = 'wbagent-notices:';

/**
 * useDismissibleNotice tracks whether a one-shot notice has been dismissed,
 * persisting in localStorage and synchronizing across all hook instances in
 * the page. Pass `showAfter` (ms) to re-show after that delay; omit for
 * permanent dismissal.
 */
export function useDismissibleNotice(
  noticeId: string,
  {showAfter}: {showAfter?: number} = {}
) {
  const storageKey = `${WBAGENT_NOTICES_PREFIX}${noticeId}`;
  const dismissedAt = useSyncExternalStore(subscribe, () => state[storageKey]);

  const dismissed =
    !isStorageWritable ||
    (dismissedAt != null &&
      // oxlint-disable-next-line react/purity
      (showAfter == null || Date.now() - dismissedAt < showAfter));

  return {
    dismissed,
    dismiss: async () => {
      if (dismissed) {
        return;
      }
      const now = Date.now();
      safeLocalStorage.setItem(storageKey, String(now));
      setDismissedAt(storageKey, now);
    },
  };
}

/**
 * hasDismissedLocally reports whether a notice was dismissed in this browser,
 * ignoring whether storage is currently writable.
 *
 * NOTE: exists to backfill legacy per-browser dismissals into the server-side
 * record; delete it with the last localStorage-backed notice.
 */
export function hasDismissedLocally(noticeId: string): boolean {
  return state[`${WBAGENT_NOTICES_PREFIX}${noticeId}`] != null;
}

function readInitialState(): Record<string, number> {
  const result: Record<string, number> = {};

  try {
    // eslint-disable-next-line no-restricted-properties
    const storage = window.localStorage;

    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i);
      if (key == null || !key.startsWith(WBAGENT_NOTICES_PREFIX)) {
        continue;
      }

      const value = Number(storage.getItem(key));
      if (Number.isFinite(value)) {
        result[key] = value;
      }
    }
  } catch {
    // localStorage not available
  }

  return result;
}

const state = readInitialState();
const isStorageWritable = safeLocalStorage.isAvailable;
const listeners = new Set<() => void>();

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function setDismissedAt(storageKey: string, value: number): void {
  state[storageKey] = value;
  for (const listener of Array.from(listeners)) {
    listener();
  }
}

window.addEventListener('storage', event => {
  if (event.key == null || !event.key.startsWith(WBAGENT_NOTICES_PREFIX)) {
    return;
  }

  // NOTE: un-dismissal not supported; ignore null newValue (entry removed).
  if (event.newValue == null) {
    return;
  }

  const value = Number(event.newValue);
  if (Number.isFinite(value)) {
    setDismissedAt(event.key, value);
  }
});
